import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/sonner";
import { NotificationPopups } from "@/components/NotificationPopups";
import { TeamCelebrations } from "@/components/TeamCelebrations";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Route, Router as WouterRouter, Switch } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { ThemeProvider } from "./contexts/ThemeContext";
import DashboardLayout from "./components/DashboardLayout";
import InvitePage from "./pages/InvitePage";
import PWAUpdatePrompt from "./components/PWAUpdatePrompt";
import { useGuardedLocation } from "./hooks/useGuardedLocation";

// Retry dynamic import — auto-reload on chunk load failure after deploy
function lazyRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return React.lazy(() =>
    factory().catch(() => {
      // Chunk failed to load — force full page reload to get new assets
      const key = "chunk-reload-ts";
      const last = sessionStorage.getItem(key);
      if (!last || Date.now() - Number(last) > 30000) {
        sessionStorage.setItem(key, String(Date.now()));
        window.location.reload();
      }
      // Return a dummy component so React doesn't throw before reload kicks in
      return { default: (() => null) as unknown as T };
    })
  );
}

const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const ContractsPage = lazyRetry(() => import("@/pages/ContractsPage"));
const KanbanPage = lazyRetry(() => import("./pages/KanbanPage"));
const FinancePage = lazyRetry(() => import("./pages/FinancePage"));
const RecruitersPage = lazyRetry(() => import("./pages/RecruitersPage"));
const CommissionsPage = lazyRetry(() => import("./pages/CommissionsPage"));
const CompaniesPage = lazyRetry(() => import("./pages/CompaniesPage"));
const SettingsPage = lazyRetry(() => import("./pages/SettingsPage"));
const TelegramSetupPage = lazyRetry(() => import("./pages/TelegramSetupPage"));
const ProfilePage = lazyRetry(() => import("./pages/ProfilePage"));
const CandidatesPage = lazyRetry(() => import("./pages/CandidatesPage"));
const CandidatePage = lazyRetry(() => import("./pages/CandidatePage"));
const ServerLogsPage = lazyRetry(() => import("./pages/ServerLogsPage"));
const InvoicePrint = lazyRetry(() => import("./pages/InvoicePrint"));
const CandidateUpdatePage = lazyRetry(
  () => import("./pages/CandidateUpdatePage")
);
const VideoInterviewPage = lazyRetry(
  () => import("./pages/VideoInterviewPage")
);
const VideoInterviewMaterialsPage = lazyRetry(
  () => import("./pages/VideoInterviewMaterialsPage")
);
const VideoInterviewsPage = lazyRetry(
  () => import("./pages/VideoInterviewsPage")
);
const VideoInterviewRecruiterRoomPage = lazyRetry(
  () => import("./pages/VideoInterviewRecruiterRoomPage")
);
const VideoInterviewClientPage = lazyRetry(
  () => import("./pages/VideoInterviewClientPage")
);

function PageLoader() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

// /oops — триггерит ErrorBoundary, чтобы посмотреть страницу-заглушку
function ThrowOnRender(): React.ReactElement {
  throw new Error("Test error: триггер /oops для проверки ErrorBoundary");
}

function Router() {
  return (
    <DashboardLayout>
      {/* Всплывашки живут внутри входа в систему: на публичных страницах
          (комната кандидата, просмотр записи заказчиком) уведомлений нет и
          спрашивать их не у кого. В комнате интервью они тоже ни к чему —
          компонент сам молчит, пока сервер не отдаст непрочитанное. */}
      <NotificationPopups />
      <TeamCelebrations />
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/" component={Dashboard} />
          <Route path="/companies" component={CompaniesPage} />
          <Route path="/contracts" component={ContractsPage} />
          <Route path="/kanban" component={KanbanPage} />
          <Route path="/finance" component={FinancePage} />
          <Route path="/commissions" component={CommissionsPage} />
          <Route path="/recruiters" component={RecruitersPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/telegram" component={TelegramSetupPage} />
          <Route path="/profile" component={ProfilePage} />
          <Route path="/candidates" component={CandidatesPage} />
          <Route path="/candidates/:id" component={CandidatePage} />
          <Route path="/video-interviews" component={VideoInterviewsPage} />
          <Route
            path="/video-interviews/:id"
            component={VideoInterviewMaterialsPage}
          />
          <Route
            path="/video-interview/:interviewId/room"
            component={VideoInterviewRecruiterRoomPage}
          />
          <Route path="/server-logs" component={ServerLogsPage} />
          <Route path="/oops" component={ThrowOnRender} />
          <Route path="/404" component={NotFound} />
          <Route component={NotFound} />
        </Switch>
      </Suspense>
    </DashboardLayout>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="light">
        <TooltipProvider>
          <Toaster />
          <PWAUpdatePrompt />
          {/*
            Роутер держит адрес комнаты, пока идёт звонок (hooks/useGuardedLocation):
            «Назад» в браузере или переход по ссылке не снимают комнату, пока
            человек не подтвердил уход. Держит на самом верху: комната рекрутёра
            живёт в роутере CRM, а комната кандидата — прямо здесь, и «Назад» с
            любой из них на любую страницу приложения обязан спросить. Вложенные
            роутеры (CRM) наследуют его сами. Вне звонка — обычный адрес
            браузера, как раньше.
          */}
          <WouterRouter hook={useGuardedLocation}>
            <Switch>
              <Route path="/invite/:token" component={InvitePage} />
              <Route path="/candidate-update/:token">
                <Suspense fallback={<PageLoader />}>
                  <CandidateUpdatePage />
                </Suspense>
              </Route>
              {/* Пока грузится код страницы кандидата — пустой фон её же цвета, а не
                синяя крутилка сверху: иначе экран трижды меняется до первого шага. */}
              <Route path="/video-interview/:token">
                <Suspense
                  fallback={
                    <div className="min-h-svh bg-white sm:bg-[#F2F4F9]" />
                  }
                >
                  <VideoInterviewPage />
                </Suspense>
              </Route>
              <Route path="/interview-view/:token">
                <Suspense fallback={<PageLoader />}>
                  <VideoInterviewClientPage />
                </Suspense>
              </Route>
              <Route path="/invoice/:id">
                <Suspense fallback={<PageLoader />}>
                  <InvoicePrint />
                </Suspense>
              </Route>
              <Route>
                <Router />
              </Route>
            </Switch>
          </WouterRouter>
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
